import { useRef, useState, useEffect } from "react";
import { Loader2, Send, AlertTriangle, RotateCcw } from "lucide-react";
import { streamForm, aiFollowUp } from "../lib/api";
import { ModelPicker, AiDropzone } from "./AiResumir";

/** Ferramenta: Perguntar ao PDF (chat com documento, respostas em streaming). */
export function PerguntarPdf() {
    const [file, setFile] = useState(null);
    const [model, setModel] = useState("claude");
    const [session, setSession] = useState(null); // {session_id, token, model, pages}
    const [messages, setMessages] = useState([]); // {role, content}
    const [question, setQuestion] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [error, setError] = useState(null);
    const bottomRef = useRef(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, streaming]);

    const reset = () => {
        setSession(null); setMessages([]); setFile(null); setQuestion(""); setError(null);
    };

    const ask = async () => {
        const q = question.trim();
        if (!q || streaming) return;
        setError(null);
        setQuestion("");
        setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
        setStreaming(true);
        const updateLast = (delta) =>
            setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + delta };
                return next;
            });
        try {
            if (!session) {
                const form = new FormData();
                form.append("file", file);
                form.append("model", model);
                form.append("question", q);
                await streamForm("/ai/perguntar", form, {
                    onMeta: (meta) => setSession({ session_id: meta.session_id, token: meta.token, model: meta.model, pages: meta.pages }),
                    onDelta: updateLast,
                    onError: (msg) => setError(msg),
                });
            } else {
                await aiFollowUp(session.session_id, session.token, q, {
                    onDelta: updateLast,
                    onError: (msg) => setError(msg),
                });
            }
        } catch (err) {
            setError(err.message);
            setMessages((m) => m.slice(0, -1)); // remove bolha vazia
        }
        setStreaming(false);
    };

    return (
        <div className="border-2 border-black bg-white hard-shadow p-4 space-y-4" data-testid="ai-perguntar">
            {!session && (
                <>
                    <ModelPicker model={model} setModel={setModel} disabled={streaming} />
                    <AiDropzone file={file} setFile={setFile} busy={streaming} />
                </>
            )}

            {session && (
                <div className="flex items-center justify-between border-2 border-black bg-[#F0F9FF] px-3 py-2" data-testid="chat-session-info">
                    <span className="font-mono-tag text-[11px] font-bold uppercase text-black">
                        {file?.name} · {session.pages} pág. · {session.model} · expira em 60 min
                    </span>
                    <button
                        onClick={reset}
                        data-testid="chat-reset-button"
                        className="inline-flex items-center gap-1 font-mono-tag text-[11px] font-bold uppercase underline decoration-[#0284C7] underline-offset-2 hover:text-[#0284C7]"
                    >
                        <RotateCcw className="w-3 h-3" aria-hidden="true" /> Trocar documento
                    </button>
                </div>
            )}

            {messages.length > 0 && (
                <div className="border-2 border-black bg-[#FAFAF9] p-3 max-h-[380px] overflow-y-auto space-y-3" data-testid="chat-messages" aria-live="polite">
                    {messages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div
                                className={`max-w-[85%] border-2 border-black px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                                    m.role === "user" ? "bg-black text-white" : "bg-white text-gray-900"
                                }`}
                                data-testid={`chat-message-${i}`}
                            >
                                {m.content}
                                {streaming && i === messages.length - 1 && m.role === "assistant" && (
                                    <span className="inline-block w-2 h-4 bg-[#0284C7] animate-pulse ml-0.5 align-middle" aria-hidden="true" />
                                )}
                            </div>
                        </div>
                    ))}
                    <div ref={bottomRef} />
                </div>
            )}

            {error && (
                <div className="border-2 border-black bg-[#FEF2F2] p-3" role="alert" data-testid="ai-error-box">
                    <p className="text-xs text-red-800 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" aria-hidden="true" /> {error}
                    </p>
                </div>
            )}

            <form
                onSubmit={(e) => { e.preventDefault(); ask(); }}
                className="flex gap-2"
            >
                <input
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder={session ? "Faça outra pergunta sobre o documento…" : "Faça a primeira pergunta sobre o PDF…"}
                    aria-label="Pergunta sobre o documento"
                    disabled={!file || streaming}
                    maxLength={2000}
                    data-testid="chat-input"
                    className="flex-1 border-2 border-black bg-white px-4 py-3 text-sm outline-none focus:border-[#0284C7] disabled:opacity-50"
                />
                <button
                    type="submit"
                    disabled={!file || !question.trim() || streaming}
                    data-testid="chat-send-button"
                    className="px-5 py-3 bg-[#0284C7] hover:bg-black disabled:bg-gray-300 text-white transition-colors"
                    aria-label="Enviar pergunta"
                >
                    {streaming ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> : <Send className="w-5 h-5" aria-hidden="true" />}
                </button>
            </form>
            <p className="font-mono-tag text-[10px] text-gray-500">
                Respostas geradas por IA podem conter erros — confira no documento. A conversa e o arquivo expiram em até 60 min.
            </p>
        </div>
    );
}
